import { CredentialEnvelope } from 'services/crypto/credentialEnvelope';
import http from 'services/http';

// Headless portal credentials (OLIO-2886 / OLIO-2882). The password is
// envelope-encrypted IN THE BROWSER (services/crypto/credentialEnvelope) before
// it touches either of these calls: olio-api relays ciphertext it cannot decrypt
// to the integration platform, and only the integration platform's headless
// worker can ever recover it. No Olio server is in the plaintext set.

export interface PortalCredentialPublicKey {
  // KMS key id/ARN — recorded inside every envelope so key rotation/granularity
  // changes never orphan stored credentials.
  keyId: string;
  // base64 SPKI DER, ready for WebCrypto importKey('spki', ...).
  publicKey: string;
  alias: string;
}

export interface SavePortalCredentialsInput {
  // Top-level field keys are camelCase; OliveBranch snake_cases them on the API.
  // The envelope's OWN keys are already snake_case data keys and pass through
  // the middleware untouched (same rule as workflow value maps).
  sourceSystem: string;
  username: string;
  credentialBlob: CredentialEnvelope;
}

export interface SavePortalCredentialsResult {
  status: string;
  sourceSystem: string;
  username: string;
  verified: boolean;
  // Whether a login check will actually run for this save (OLIO-3131): false means no anchor
  // exists anywhere (no recording, no portal home URL), so no verdict will ever arrive and
  // the modal must not wait for one.
  verifyAnchored?: boolean;
}

export const portalCredentialQueryKeys = {
  base: ['portalCredentials'] as const,
  publicKey: () => ['portalCredentials', 'publicKey'] as const,
};

// The key is immutable for the life of the environment — cache aggressively.
export async function getPortalCredentialPublicKey() {
  return http.get<PortalCredentialPublicKey>('portal_credentials/public_key').then((res) => res.data);
}

export async function savePortalCredentials(input: SavePortalCredentialsInput) {
  return http.post<SavePortalCredentialsResult>('portal_credentials', input).then((res) => res.data);
}
